import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, signal } from '@angular/core';
import { ModalService } from '@src/shared/modal/modal.service';
import { Behavior, BehaviorService } from '../../../../woo_services.module/BehaviorService';
import { ExtraProductFormatField, ProductFormatService } from '../../../../woo_services.module/ProductFormatService';
import { ExtraPublisherField, PublisherService } from '../../../../woo_services.module/PublisherService';
import { RoutingService } from '../../../../woo_services.module/RoutingService';
import { SystemService } from '../../../../woo_services.module/SystemService';
import { TranslationService } from '../../../../woo_services.module/TranslationService';
import {
  ProductFormat,
  Publisher,
  SlotType,
  SlotTypeEstimates,
  wooId,
} from '../../../../woo_services.module/shared-types';
import { ProductsPageUtilityService } from '../../shared/products-page-utility-service';
import { BehaviorParams } from './behavior-form.component';

@Component({
  selector: 'manage-behaviors',
  templateUrl: './manage-behaviors.component.html',
  styleUrls: ['../../shared/table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBehaviors implements OnInit {
  @Input('headerPos') parentHeader: HTMLElement;
  show = {
    newBehaviorForm: false,
    estimatesHaveChanged: false,
    updatingEstimates: false,
    loading: true,
    isEditing: false,
  };

  readonly tableHeaders = [
    'Publicist',
    'Placering',
    'Segment',
    'Tagg',
    'Bokningsbar',
    'Preroll',
    'Midroll',
    'Postroll',
    'Preroll',
    'Midroll',
    'Postroll',
  ];

  readonly staticFields = 5;

  publishers: Publisher[] = [];
  selectedPublisher: Publisher;
  behaviors: Behavior[] = [];
  selectedBehavior: Behavior;
  productFormats: ProductFormat[];
  selectedProductFormat: ProductFormat;
  behaviorRows: BehaviorRow[];
  includedProductFormatSlotTypes: Record<wooId, Record<SlotType, boolean>> = {};
  shareOfTotal: number;
  saving = signal<boolean>(false);
  translateProductFormat: (args: any) => string;

  constructor(
    private publisherService: PublisherService,
    private productFormatService: ProductFormatService,
    private behaviorService: BehaviorService,
    private utilityService: ProductsPageUtilityService,
    private routingService: RoutingService,
    private systemService: SystemService,
    private translationService: TranslationService,
    private changeDetection: ChangeDetectorRef,
    private modalService: ModalService,
  ) {}

  ngOnInit(): void {
    this.translateProductFormat = this.translationService.convertProductFormat;
    this.publisherService
      .getPublishers(ExtraPublisherField.enabled)
      .then((publishers) => {
        this.publishers = publishers;
        this.publishers = publishers.filter((p) => p.enabled);
      })
      .then(() => {
        return this.productFormatService
          .getProductFormats(
            ExtraProductFormatField.enabled,
            ExtraProductFormatField.slotTypes,
            ExtraProductFormatField.behaviorShares,
            ExtraProductFormatField.behaviorWithdrawalLimits,
          )
          .then((productFormats) => {
            this.productFormats = productFormats;
            this.productFormats = productFormats.filter((f) => f.enabled);
            this.includedProductFormatSlotTypes = this.utilityService.setIncludedProductFormatSlotTypes(
              this.productFormats,
            );
          });
      })
      .then(() => {
        return this.getAndBuildBehaviors();
      })
      .finally(() => {
        this.show.loading = false;
      });
    this.systemService.getValue('behavior_estimate_share_of_total').then((shareOfTotal) => {
      this.shareOfTotal = shareOfTotal;
    });
    this.show.estimatesHaveChanged = this.routingService.getState('behaviorEstimatesChanged');
  }

  async getAndBuildBehaviors(): Promise<void> {
    return this.behaviorService.getBehaviors().then((behaviors) => {
      this.behaviors = behaviors;
      this.buildBehaviorRows();
      this.changeDetection.markForCheck();
    });
  }

  setEditing(value: boolean): void {
    this.show.isEditing = value;
  }

  cancelEditing(): Promise<void> {
    this.setEditing(false);
    return this.getAndBuildBehaviors();
  }

  async updateShareOfTotal(): Promise<void> {
    this.saving.set(true);
    this.systemService
      .updateShareOfTotal({ behavior_estimate_share_of_total: this.shareOfTotal })
      .then((settings) => {
        if (this.shareOfTotal === settings.behavior_estimate_share_of_total) {
          this.shareOfTotal = settings.behavior_estimate_share_of_total;
          this.show.estimatesHaveChanged = true;
        }
      })
      .finally(() => {
        this.saving.set(false);
      });
  }
  async addBehavior(deviceGroupParams: BehaviorParams): Promise<void> {
    this.behaviorService
      .addBehavior(deviceGroupParams)
      .then((behavior) => {
        this.show.newBehaviorForm = false;
        this.behaviors.push(behavior);
        this.show.estimatesHaveChanged = true;
      })
      .then(() => {
        this.buildBehaviorRows();
        this.hideNewBehavior();
      });
  }

  async save(): Promise<void> {
    const filterActive = this.selectedBehavior || this.selectedPublisher || this.selectedProductFormat;
    const textBlocks = ['Är du säker på att du vill spara dina ändringar?'];
    if (filterActive) {
      textBlocks.push('Notera att även ändringar i filtrerade rader sparas.');
    }
    this.modalService
      .openConfirm({
        title: 'Bekräfta ändringar',
        texts: textBlocks,
        confirmLabel: 'Spara',
        cancelLabel: 'Avbryt',
      })
      .then((status) => {
        if (status.confirm) {
          const loaderModal = this.modalService.openLoader('Sparar ändringar');
          this.show.loading = true;
          this.show.updatingEstimates = true;
          this.behaviorService
            .updateBehaviors(this.behaviors)
            .then(() => {
              this.show.estimatesHaveChanged = true;
            })
            .finally(() => {
              loaderModal.close();
              this.setEditing(false);
              this.getAndBuildBehaviors();
              this.show.loading = false;
              this.show.updatingEstimates = false;
              this.changeDetection.markForCheck();
            });
        }
      });
  }

  showNewBehavior(): void {
    this.show.newBehaviorForm = true;
  }

  hideNewBehavior(): void {
    this.show.newBehaviorForm = false;
  }

  toggleActiveForBehavior(behavior: Behavior): void {
    this.behaviorService.updateBehavior(behavior);
  }

  getSpecificBehavior(behaviorId: wooId): Behavior {
    return this.behaviors.find((behavior) => behavior.id === behaviorId);
  }

  getSpecificBehaviorShares(behaviorId: wooId, publisherId: wooId, productFormatId: wooId): SlotTypeEstimates {
    return this.behaviors.find((behavior) => behavior.id === behaviorId).shares[publisherId][productFormatId];
  }

  getSpecificBehaviorLimits(behaviorId: wooId, publisherId: wooId, productFormatId: wooId): SlotTypeEstimates {
    return this.behaviors.find((behavior) => behavior.id === behaviorId).withdrawal_limits[publisherId][
      productFormatId
    ];
  }

  rowVisible(obj: BehaviorRow): boolean {
    const publisherFiltered = this.selectedPublisher && obj.publisherId !== this.selectedPublisher.id;
    const selectedBehaviorFiltered = this.selectedBehavior && obj.behaviorId !== this.selectedBehavior.id;
    const productFormatFiltered = this.selectedProductFormat && obj.productFormatId !== this.selectedProductFormat.id;
    return !(publisherFiltered || selectedBehaviorFiltered || productFormatFiltered);
  }

  private buildBehaviorRows(): void {
    let behaviorRows: BehaviorRow[] = [];
    this.behaviors.map((behavior) => {
      this.publishers.map((publisher) => {
        this.utilityService.getPublisherSpecificProductFormats(publisher, this.productFormats).map((productFormat) => {
          behaviorRows = [
            ...behaviorRows,
            {
              publisher: publisher.name,
              publisherId: publisher.id,
              productFormat: productFormat.name,
              productFormatId: productFormat.id,
              behavior: behavior.name,
              behaviorId: behavior.id,
              tag: behavior.tag,
              active: behavior.active,
              sharesPreroll: behavior.shares[publisher.id][productFormat.id].preroll,
              sharesMidroll: behavior.shares[publisher.id][productFormat.id].midroll,
              sharesPostroll: behavior.shares[publisher.id][productFormat.id].postroll,
              withdrawalLimitsPreroll: behavior.withdrawal_limits[publisher.id][productFormat.id].preroll,
              withdrawalLimitsMidroll: behavior.withdrawal_limits[publisher.id][productFormat.id].midroll,
              withdrawalLimitsPostroll: behavior.withdrawal_limits[publisher.id][productFormat.id].postroll,
            },
          ];
        });
      });
    });
    this.behaviorRows = this.sortRows(behaviorRows);
  }

  private sortRows(rows: BehaviorRow[]): BehaviorRow[] {
    const sorted = rows.sort((a, b) => {
      if (a.publisher !== b.publisher) return a.publisher.localeCompare(b.publisher);
      if (a.behavior !== b.behavior) return a.behavior.localeCompare(b.behavior);
      return a.productFormat.localeCompare(b.productFormat);
    });
    return sorted;
  }
}
interface BehaviorRow {
  publisher: string;
  publisherId?: wooId;
  productFormat: string;
  productFormatId: wooId;
  behavior: string;
  behaviorId: wooId;
  tag: string;
  active: boolean;
  sharesPreroll?: number;
  sharesMidroll?: number;
  sharesPostroll?: number;
  withdrawalLimitsPreroll?: number;
  withdrawalLimitsMidroll?: number;
  withdrawalLimitsPostroll?: number;
}
